import React from 'react';
import styled from 'styled-components';
import {
  FaBalanceScale,
  FaCertificate,
  FaFileAlt,
  FaMoneyBillWave,
  FaChalkboardTeacher,
  FaHandshake
} from 'react-icons/fa';
import Button from '../Button';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedSection from '../Animation/AnimatedSection';

const ServicesSection = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.theme.colors.secondary};
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  width: 100%;

  align-items: stretch;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: ${props => props.theme.colors.white};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.boxShadow};
  
  height: 25rem;
  display: flex;
  flex-direction: column;

  cursor: pointer;
  transition: box-shadow 0.3s ease, transform 0.2s ease;

  @media screen and (max-width: 768px) {
    text-align: center;
    align-items: center;
  }
`;

const IconContainer = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.grayLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
`;

const ServiceTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  flex-grow: 1;
  text-align: justify;
  text-justify: inter-word;
`;

const ButtonWrapper = styled.div`
  margin-top: 1rem;
`;

const Services = () => {
  const services = [
    {
      icon: <FaBalanceScale />,
      title: "Representação Legal",
      description:
        "Empresas estrangeiras que desejam regularizar seus produtos nos órgãos reguladores brasileiros precisam de uma empresa estabelecida no país para atuar como representante legal."
    },
    {
      icon: <FaCertificate />,
      title: "Consultoria em Certificação",
      description:
        "Consultoria em certificação para impulsionar produtos ao mercado brasileiro com confiança e excelência técnica."
    },
    {
      icon: <FaFileAlt />,
      title: "Documentação Técnica",
      description:
        "Documentação técnica, registro de produtos e suporte especializado em auditorias."
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Redução de Custos",
      description:
        "Estratégias eficientes para otimizar custos em certificação e P&D, aprimorando processos produtivos e garantindo total conformidade com os requisitos normativos."
    },
    {
      icon: <FaChalkboardTeacher />,
      title: "Treinamentos",
      description:
        "Treinamentos presenciais e online para elevar a proficiência técnica em normas, processos e certificações."
    },
    {
      icon: <FaHandshake />,
      title: "Suporte com Organismos de Certificação",
      description:
        "Atuação direta com laboratórios e entidades certificadoras para otimizar etapas, reduzir atrasos e melhorar a eficiência do processo."
    }
  ];

  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5, delay: 0.2 }
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: { duration: 0.3 }
    }
  };
  
  return (
    <ServicesSection id="servicos">
      <div className="container">
        <SectionTitle
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Nossos Serviços
        </SectionTitle>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <AnimatedSection 
              key={index} 
              animation="fadeIn" 
              delay={0.1 * index}
              duration={0.6}
            >
              <ServiceCard 
                whileHover={{ 
                  y: -8,
                  boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)'
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <IconContainer
                  variants={iconVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                >
                  {service.icon}
                </IconContainer>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ButtonWrapper>
                  <Button 
                    tertiary
                    size="lg"
                    as={motion.a}
                    href="#contato"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Saiba mais
                  </Button>
                </ButtonWrapper>
              </ServiceCard>
            </AnimatedSection>
          ))}
        </ServicesGrid>
      </div>
    </ServicesSection>
  );
};

export default Services;
